import { z } from "zod";
import { TEMPLATE_ALLOWED_VARS } from "@goppy/domain";
import { aiFlowBandSchema } from "./ai/index.js";

/**
 * Biến ĐƯỢC PHÉP trong template flow — MỘT NGUỒN SỰ THẬT (`StepRunner.TEMPLATE_ALLOWED_VARS` của
 * domain). Admin UI (T5) hiện đúng danh sách này cho người sửa template; service validate cũng dùng
 * chính nó ⇒ UI KHÔNG BAO GIỜ lệch với luật (biến lạ ⇒ 422, DP-3).
 */
export const AI_FLOW_TEMPLATE_ALLOWED_VARS: readonly string[] = TEMPLATE_ALLOWED_VARS;

/**
 * Shared DTO — Admin config flow AI-first (US-AI.F3-0232 · @UC9400 · @AI-ONB-02 @AI-DIS-22 @SEC-CTX-02).
 *
 * Một nguồn sự thật cho api (controller + DTO validate biên) và admin web (màn Config T5).
 *
 * BẤT BIẾN:
 * - Flow KHÔNG tạo/xoá được qua API — chỉ OVERRIDE phần `configurable` của flow có sẵn trong FLOW_CATALOG
 *   (code). flowId lạ ⇒ 404.
 * - Kế thừa: default catalog ⊕ override system ⊕ override group (group thắng system thắng default).
 *   Field VẮNG trong PATCH = "không đụng"; `null` tường minh = "xoá override, kế thừa lại".
 * - DP-3: template CHỈ narration. Biến trong template phải ∈ allowlist ({tenQuy},{soTien},{tenNguoiDung},
 *   {tenDot},{soViec}) — biến lạ ⇒ 422 (validate ở service, dùng TEMPLATE_ALLOWED_VARS của StepRunner:
 *   một allowlist, không nhân bản). Template KHÔNG BAO GIỜ sinh số tiền/logic.
 * - `.strict()` — chặn key lạ tại biên (C5 fail-closed input).
 * - `thresholds`: map tên → SỐ NGUYÊN (không phải tiền ⇒ không BigInt; là ngưỡng đếm việc/phút).
 */

/** Scope override: 'system' (toàn hệ thống) | 'group' (một nhóm — đè system). */
export const aiFlowScopeSchema = z.enum(["system", "group"]);
export type AiFlowScope = z.infer<typeof aiFlowScopeSchema>;

/** Ngưỡng: chỉ số nguyên không âm, hữu hạn (KHÔNG float, KHÔNG chuỗi ⇒ luật không nuốt rác). */
export const aiFlowThresholdsSchema = z.record(z.string().min(1).max(64), z.number().int().min(0));
export type AiFlowThresholds = z.infer<typeof aiFlowThresholdsSchema>;

/** Phần admin cấu hình được (mirror `FlowConfigurable` của domain). */
export const aiFlowConfigurableSchema = z
  .object({
    enabled: z.boolean(),
    /**
     * 🔴 US-AI-0439-S05 (@UC9688) — BẬC (`band`) LỌC, `priority` SẮP XẾP. Closed-set, KHÁC `priority`
     * (số [0..1000]): bậc mà nhận số tuỳ ý thì mất luôn ý nghĩa "bậc".
     */
    band: aiFlowBandSchema,
    /**
     * Loại việc — quyết định flow có vào badge / có tham gia chọn bậc không. **Chỉ ĐỌC với admin**:
     * đây là ngữ nghĩa của flow, không phải cấu hình vận hành (cùng lý do `trigger.roles` bị cấm sửa).
     */
    /**
     * 🔴 US-AI-0441-S03 (@UC9698) — THÊM `idle_prompt` (câu nói khi không có việc, nối vào lời chào;
     * không phải thẻ việc ⇒ không vào badge, không tham gia chọn bậc).
     *
     * ⚠️ Danh sách này là BẢN SAO của `FLOW_KINDS` ở `@goppy/domain` — contract không import được
     * domain (phụ thuộc ngược chiều). Cặp được canh bằng test: thêm kind ở domain mà quên ở đây thì
     * `toConfigurableDto` **đỏ lúc typecheck**, đó là lưới thật (đã bắt đúng ca này).
     */
    itemKind: z.enum(["task", "hygiene", "warning", "reactive", "idle_prompt"]),
    priority: z.number().int(),
    templateVi: z.string(),
    templateEn: z.string(),
    /** Câu chữ thẻ việc (US-AI-0439-S02) — admin sửa được, cặp Vi/En đi cùng nhau. */
    detailVi: z.string().optional(),
    detailEn: z.string().optional(),
    nextVi: z.string().optional(),
    nextEn: z.string().optional(),
    ctaLabelVi: z.string().optional(),
    ctaLabelEn: z.string().optional(),
    thresholds: aiFlowThresholdsSchema.optional(),
    requiresHeavyConfirm: z.boolean().optional(),
  })
  .strict();
export type AiFlowConfigurable = z.infer<typeof aiFlowConfigurableSchema>;

/** Override thô đã lưu ở một scope (null = không override field đó). */
export const aiFlowOverrideSchema = z
  .object({
    scope: aiFlowScopeSchema,
    groupId: z.string().uuid().nullable(),
    enabled: z.boolean().nullable(),
    priority: z.number().int().nullable(),
    band: aiFlowBandSchema.nullable(),
    templateVi: z.string().nullable(),
    templateEn: z.string().nullable(),
    detailVi: z.string().nullable(),
    detailEn: z.string().nullable(),
    nextVi: z.string().nullable(),
    nextEn: z.string().nullable(),
    ctaLabelVi: z.string().nullable(),
    ctaLabelEn: z.string().nullable(),
    thresholds: aiFlowThresholdsSchema.nullable(),
    updatedBy: z.string(),
    updatedAt: z.string(),
  })
  .strict();
export type AiFlowOverride = z.infer<typeof aiFlowOverrideSchema>;

/** Một flow trong danh sách admin: định danh catalog + default + config HIỆU LỰC + override đã lưu. */
export const adminAiFlowItemSchema = z
  .object({
    flowId: z.string(),
    /** Mã scenario registry (trace docs/ai-scenarios/registry.json) — nhiều flow có thể chung 1 mã. */
    code: z.string(),
    kind: z.enum(["proactive", "reactive"]),
    rule: z.string(),
    roles: z.array(z.string()),
    deepLinks: z.array(z.string()),
    /** Default BẤT BIẾN từ FLOW_CATALOG (code) — admin thấy mình đang lệch khỏi cái gì. */
    defaults: aiFlowConfigurableSchema,
    /** Config HIỆU LỰC sau kế thừa (default ⊕ system ⊕ group). */
    effective: aiFlowConfigurableSchema,
    /** Override đã lưu ở từng scope (nếu có). */
    systemOverride: aiFlowOverrideSchema.nullable(),
    groupOverride: aiFlowOverrideSchema.nullable(),
  })
  .strict();
export type AdminAiFlowItem = z.infer<typeof adminAiFlowItemSchema>;

/** Query GET /v1/admin/ai/flows — `groupId` tuỳ chọn: có ⇒ tính config hiệu lực cho NHÓM đó. */
export const adminAiFlowsQuerySchema = z
  .object({
    groupId: z.string().uuid().optional(),
  })
  .strict();
export type AdminAiFlowsQuery = z.infer<typeof adminAiFlowsQuerySchema>;

/** Response GET /v1/admin/ai/flows. */
export const adminAiFlowsResponseSchema = z
  .object({
    groupId: z.string().nullable(),
    items: z.array(adminAiFlowItemSchema),
  })
  .strict();
export type AdminAiFlowsResponse = z.infer<typeof adminAiFlowsResponseSchema>;

/**
 * Body PATCH /v1/admin/ai/flows/:flowId — sửa OVERRIDE.
 *
 * `scope='group'` BẮT BUỘC kèm `groupId` (refine) — không có "override nhóm mồ côi". Field vắng = không
 * đụng; `null` = xoá override (kế thừa lại). Không field nào ⇒ 400 (PATCH rỗng = gọi nhầm, fail-loud).
 */
export const patchAdminAiFlowSchema = z
  .object({
    scope: aiFlowScopeSchema,
    groupId: z.string().uuid().optional(),
    enabled: z.boolean().nullable().optional(),
    band: aiFlowBandSchema.nullable().optional(),
    priority: z.number().int().min(0).max(1000).nullable().optional(),
    templateVi: z.string().min(1).max(2000).nullable().optional(),
    templateEn: z.string().min(1).max(2000).nullable().optional(),
    detailVi: z.string().min(1).max(2000).nullable().optional(),
    detailEn: z.string().min(1).max(2000).nullable().optional(),
    nextVi: z.string().min(1).max(2000).nullable().optional(),
    nextEn: z.string().min(1).max(2000).nullable().optional(),
    ctaLabelVi: z.string().min(1).max(120).nullable().optional(),
    ctaLabelEn: z.string().min(1).max(120).nullable().optional(),
    thresholds: aiFlowThresholdsSchema.nullable().optional(),
  })
  .strict()
  .refine((b) => (b.scope === "group" ? typeof b.groupId === "string" : b.groupId === undefined), {
    message: "scope='group' cần groupId; scope='system' KHÔNG được kèm groupId",
    path: ["groupId"],
  })
  .refine(
    (b) =>
      b.enabled !== undefined ||
      b.band !== undefined ||
      b.priority !== undefined ||
      b.templateVi !== undefined ||
      b.templateEn !== undefined ||
      b.detailVi !== undefined ||
      b.detailEn !== undefined ||
      b.nextVi !== undefined ||
      b.nextEn !== undefined ||
      b.ctaLabelVi !== undefined ||
      b.ctaLabelEn !== undefined ||
      b.thresholds !== undefined,
    { message: "PATCH rỗng — cần ít nhất một field override" },
  );
export type PatchAdminAiFlowRequest = z.infer<typeof patchAdminAiFlowSchema>;
